import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Base URL of Maven-platform-Server (the main backend).
 *
 * REACT_APP_MAVEN_SERVER is the variable the rest of the app already uses (SignPage, InfoRequestPage,
 * ServiceQuestionnairePage); the fallback is the same production URL those pages fall back to.
 * Trailing slashes are trimmed so `${MAVEN_API_BASE}/client/...` never produces a double slash.
 */
export const MAVEN_API_BASE = (
  process.env.REACT_APP_MAVEN_SERVER || 'https://sea-lion-app-7wbpx.ondigitalocean.app'
).trim().replace(/\/+$/, '');

/** An HTTP error from Maven, carrying the status and the server's machine-readable `error` code. */
export class MavenApiError extends Error {
  constructor(message, status, code, body) {
    super(message);
    this.name = 'MavenApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/**
 * Authenticated JSON calls to Maven with the Auth0 access token.
 *
 * Deliberately not the shared AuthenticatedAxios: that client points at User_Platform_Server and
 * redirects to login on every 401/403, while here a 4xx (wrong code, expired code) is a normal
 * state the page must show. Only "the session itself is gone" sends the user back to login.
 */
export function useMavenApi() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  return useCallback(async (path, { method = 'GET', body } = {}) => {
    let token;
    try {
      token = await getAccessTokenSilently();
    } catch (e) {
      // Refresh failed (session expired / consent needed): log in again and come back here.
      if (e && (e.error === 'login_required' || e.error === 'consent_required')) {
        await loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } });
      }
      throw new MavenApiError('Your session has expired. Please sign in again.', 401, 'session-expired');
    }

    let res;
    try {
      res = await fetch(`${MAVEN_API_BASE}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new MavenApiError('Could not reach the server. Check your connection and try again.', 0, 'network');
    }

    let data = null;
    try { data = await res.json(); } catch { /* empty or non-JSON body */ }

    if (!res.ok) {
      const code = (data && (data.error || data.code)) || undefined;
      const message = (data && (data.message || (typeof data.error === 'string' && data.error))) || `Request failed (${res.status})`;
      throw new MavenApiError(message, res.status, code, data);
    }
    return data;
  }, [getAccessTokenSilently, loginWithRedirect]);
}

export default useMavenApi;
